<template>
  <div
    class="tw-w-full tw-flex"
    :class="{ 'tw-justify-end': isAuthored, 'tw-justify-start': !isAuthored }"
  >
    <div class="tw-flex tw-gap-1.5 tw-max-w-[90%]">
      <div v-if="!isAuthored">
        <WdsAvatar
          v-if="isPending && content === 'Thinking...'"
          path="/gifs/worksome-assistant-thinking.gif"
          xs
        />
        <WdsIcon v-else :name="WdsIconNameEnum.WORKSOME_ASSISTANT" xs class="tw-text-indigo-400" />
      </div>
      <div class="tw-flex tw-flex-col">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="tw-rounded tw-p-2 ai-message-content" :class="classes" v-html="content" />
        <div
          class="tw-flex tw-mt-1"
          :class="{ 'tw-justify-between': !isAuthored, 'tw-justify-end': isAuthored }"
        >
          <time
            v-if="timestamp || isAuthored"
            class="tw-text-gray-600 tw-text-sm tw-self-center"
            :datetime="timestamp"
            :title="moment(timestamp).format('lll')"
          >
            {{ timestamp ? moment(timestamp).fromNow() : 'just now' }}
          </time>
          <div
            v-if="!isAuthored && content !== 'Please wait...' && content !== 'Thinking...'"
            class="tw-flex tw-justify-end"
          >
            <span class="tw-text-gray-600 tw-text-sm tw-self-center">Rate answer:</span>
            <div class="tw-flex tw-gap-1">
              <WdsButtonGhost xs :title="$t('Like')" @click="handleRating('like')">
                <WdsStack align-center gap-sm>
                  <WdsIcon
                    name="thumbs-up"
                    :size="14"
                    :color="rating === 'like' ? '#047E43' : null"
                  />
                </WdsStack>
              </WdsButtonGhost>
              <WdsButtonGhost xs :title="$t('Dislike')" @click="handleRating('dislike')">
                <WdsStack align-center gap-sm>
                  <WdsIcon
                    name="thumbs-down"
                    :size="14"
                    :color="rating === 'dislike' ? '#D12323' : null"
                  />
                </WdsStack>
              </WdsButtonGhost>
            </div>
          </div>
        </div>
      </div>
      <WdsAvatar v-if="isAuthored" :path="avatarPath" xs class="" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import {
  WdsAvatar,
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsStack,
} from '@worksome/wds-components'
import moment from 'moment'
import { ai } from '@/api/vue-api'
import { NetworkError } from '@/utils/notification'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import enums from '@/utils/enum'

interface Props {
  isAuthored?: boolean
  isPending?: boolean
  avatarPath: string
  content?: string
  messageUuid?: string
  rating?: string | null
  timestamp?: string
}

const props = defineProps<Props>()
const emit = defineEmits(['update:rating'])

const rating = computed({
  get: () => props.rating,
  set: (value) => emit('update:rating', value),
})

const classes = computed(() => ({
  'tw-bg-primary': props.isAuthored,
  'tw-bg-gray-100': !props.isAuthored,
  'tw-text-white': props.isAuthored && !props.isPending,
  'tw-text-primary': !props.isAuthored && !props.isPending,
  'tw-text-gray-600': props.isPending,
}))

const handleRating = async (newRating: string) => {
  if (!props.messageUuid) return

  try {
    const response = await ai.rateMessage(props.messageUuid, newRating)
    rating.value = response.data.rating
  } catch (error) {
    new NetworkError({
      msg: $t('An error occurred while rating the message.'),
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  }
}
</script>

<style lang="scss">
.ai-message-content {
  p {
    margin: 0;
  }
  p ~ h3 {
    margin-top: 11px;
  }
  p ~ h4 {
    margin-top: 11px;
  }
  h3 {
    font-size: 1.1em;
    margin-top: 0;
  }
  h4 {
    font-size: 1.1em;
    margin-top: 0;
  }
  ul, ol {
      margin-top: 11px;
    ul {
      margin-top: 0;
    }
  }
  pre {
    font-size: 9px;
  }
  blockquote {
    font-size: 14px;
    margin-bottom: 0px;
  }
  /* Initial minimalist table CSS - should get a proper design applied in the future */
  table {
    margin-top: 11px;
    margin-bottom: 11px;
    border-collapse: collapse;
    tr:nth-child(even) {
      background: #e0e0e0;
    }
    tr:hover {
      background: #d1d1d1;
    }
    th {
      background: #ccc;
      border: 1px solid #000;
      vertical-align: top;
      padding: 4px;
    }
    td {
      border: 1px solid #000;
      vertical-align: top;
      padding: 4px;
    }
  }
}
</style>
